import dayjs from 'dayjs';
import { useEffect, useRef } from 'react';
import {
  type Control,
  type UseFormSetValue,
  useFormState,
  useWatch,
} from 'react-hook-form';

import type { OrderFormV3FieldValues } from '../OrderFormV3Context';

/**
 * Triggers: `loadAt`, `jobStartAt`, `jobStartSameAsOrderStart` (watched).
 * Writes:   `jobStartAt` — pinned to `loadAt` while "same as order start" is
 *           checked, dirtied when `loadAt` is dirty or the user cleared a
 *           separate truck start time; `jobStartSameAsOrderStart` —
 *           auto-checked when both timestamps exist and land on the same
 *           minute (collapsing the split job-start row back into the single
 *           date row).
 * Guards:   the mirror runs only while the box is checked, and dirties only on
 *           those two user-driven triggers, so both mount seeding and the mount
 *           auto-check stay clean writes; the auto-check only when both values
 *           are set. Both comparisons are minute-granular: the pickers expose
 *           only minutes, so sub-minute drift between the saved timestamps
 *           would otherwise strand the split row forever.
 * Legacy:   the legacy form's loadAt/jobStartAt sync. Legacy sent
 *           `job_start_at` on every update regardless of dirty state; v3's
 *           PATCH is dirty-gated, so the mirror has to carry the dirty bit
 *           across or the field never reaches the payload.
 * Test:     __tests__/OrderFormV3Context.test.tsx ("loadAt/jobStartAt sync").
 */
export function useLoadAtJobStartSync({
  control,
  setValue,
}: {
  control: Control<OrderFormV3FieldValues>;
  setValue: UseFormSetValue<OrderFormV3FieldValues>;
}): void {
  const loadAt = useWatch({ control, name: 'loadAt' });
  const jobStartAt = useWatch({ control, name: 'jobStartAt' });
  const jobStartSameAsOrderStart = useWatch({
    control,
    name: 'jobStartSameAsOrderStart',
  });
  const { dirtyFields } = useFormState({ control, name: 'loadAt' });
  const previousSameAsOrderStartRef = useRef(jobStartSameAsOrderStart);

  // While the box is checked, jobStartAt mirrors loadAt. The mirror dirties
  // rather than writing clean like the cascade effects do: the sparse PATCH
  // emits only dirty keys, so a clean write drops job_start_at from the payload
  // and the order moves while its jobs stay behind. Both user-driven triggers
  // have to carry the bit — editing the order start, and clearing a separate
  // truck start time, which re-checks the box without touching loadAt.
  useEffect(() => {
    const boxJustBecameChecked =
      jobStartSameAsOrderStart && !previousSameAsOrderStartRef.current;
    previousSameAsOrderStartRef.current = jobStartSameAsOrderStart;

    // The auto-check below also flips the box on mount, but only ever when the
    // two instants already agree — so a transition that genuinely replaces a
    // different job start is the user clearing a separate truck start time.
    const jobStartDiffersFromOrderStart =
      Boolean(loadAt) &&
      Boolean(jobStartAt) &&
      !dayjs(jobStartAt).isSame(dayjs(loadAt), 'minute');
    const userClearedSeparateTruckTime =
      boxJustBecameChecked && jobStartDiffersFromOrderStart;

    if (jobStartSameAsOrderStart) {
      setValue('jobStartAt', loadAt, {
        shouldDirty: Boolean(dirtyFields.loadAt) || userClearedSeparateTruckTime,
      });
    }
    // Legacy deps verbatim ([watchedLoadAt, watchedJobStartSameAsOrderStart])
    // — setValue and the dirty map are read via closure, exactly as legacy
    // reads setValue.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadAt, jobStartSameAsOrderStart]);

  // Matching minutes re-check the box.
  useEffect(() => {
    if (loadAt && jobStartAt && dayjs(loadAt).isSame(dayjs(jobStartAt), 'minute')) {
      setValue('jobStartSameAsOrderStart', true, { shouldDirty: false });
    }
    // Legacy deps verbatim ([watchedLoadAt, watchedJobStartAt]).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadAt, jobStartAt]);
}
